import React, { useEffect, useMemo } from 'react';
import { StyledRowSubComponent } from './styled';
import { Col, Row } from 'react-styled-flexboxgrid';
import Button from '../../../../../../components/Button';
import Divider from '../../../../../../components/Divider';
import ImageUpload from '../../../../../../components/ImageUpload';
import Input from '../../../../../../components/Input';
import Map from '../../../../../../components/Map';
import TextArea from '../../../../../../components/TextArea';
import ButtonGroup from '../../../../../../components/ButtonGroup';
import { makeStyles } from '../../../../../../utils/styles';
import { ApiDepartments } from '../../../../../../api/departments';
import Modal from '../../../../../../components/Modal';
import FormDepartment from '../../../../../../forms/FormDepartment';
import FormConfirmDelete from '../../../../../../forms/FormConfirmDelete';
import { ApiProfessions } from '../../../../../../api/professions';
import TableDepartments from '../TableDepartments';
import { findByID, getImageUrl, parseDepartment } from '../../../../../../utils/helpers';
import { useApi } from '../../../../../../hooks/useApi';
import { toast } from 'react-toastify';
import { useManage } from '../../../../../../hooks/useManage';
import { OPERATIONS, ROLES } from '../../../../../../utils/constans';
import Box from '../../../../../../components/Box';
import LoaderImage from '../../../../../../components/LoaderImage';
import Icon from '../../../../../../components/Icon';
import { ApiSuppliers } from '../../../../../../api/suppliers';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    buttons: css`
        margin: 20px 0;
        justify-content: flex-end;
    `,
    dividerBottom: css`
        margin: 15px 0;
    `,
    topDivider: css`
        margin-bottom: 30px;
    `,
    boxLeft: css`
        margin-right: 40px;
    `,
    boxRight: css`
        margin-left: 40px;
    `,
}));

const RowSubComponent = ({ row, onClickEdit, onClickRemove }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const [{ operation, id: activeID }, setState] = useManage();

    const role = useSelector(state => state.auth?.user?.user?.role?.type);

    // Setup request
    const [{ data: professions, loading: professionsLoading }] = useApi(ApiProfessions.get);
    const [{ data: departments, loading: departmentsLoading }, getDepartments] = useApi(ApiDepartments.get, { manual: true });
    const [{ loading: createDepartmentLoading }, createDepartment] = useApi(ApiDepartments.create, { manual: true });
    const [{ loading: updateDepartmentLoading }, updateDepartment] = useApi(ApiDepartments.update, { manual: true });
    const [{ loading: deleteDepartmentLoading }, deleteDepartment] = useApi(ApiDepartments.delete, { manual: true });
    const [{ data: availableManagersData, loading: availableManagersLoading }, getAvailableManagers] = useApi(ApiSuppliers.getAvailableManagers, { manual: true });

    useEffect(() => {
        getDepartments({ place_id: row.original?.id });
        getAvailableManagers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Memoize data
    const active = useMemo(
        () => departments && parseDepartment(findByID(departments, activeID)),
        [departments, activeID]
    );

    const availableManagers = useMemo(
        () => {
            if (availableManagersData) {
                if(operation === OPERATIONS.EDIT && active?.user_supplier) {
                    return [active.user_supplier, ...availableManagersData];
                } else {
                    return availableManagersData;
                }
            }
        },
        [active, operation, availableManagersData]
    );

    // Click handlers
    const handleClickAdd = () => setState(OPERATIONS.ADD, null);

    const handleClickDuplicate = id => () => setState(OPERATIONS.DUPLICATE, id);

    const handleClickEdit = id => () => setState(OPERATIONS.EDIT, id);

    const handleClickDelete = id => () => setState(OPERATIONS.DELETE, id);

    const handleClickCancelModal = () => setState(OPERATIONS.READ, null);

    // Submit handlers
    const handleSubmit = async data => {
        const parsedData = {
            name: data?.name,
            phone: data?.phone,
            place_id: row.original?.id,
            professions_ids: data?.professions_ids,
            user_supplier_id: data?.manager
        }

        if (activeID) parsedData['department_id'] = activeID;

        if (operation === OPERATIONS.EDIT) {
            //UPDATE
            const res = await updateDepartment(parsedData);
            if (res?.data) {
                toast.success(t('Dział został zaktualizowany'));
                setState(OPERATIONS.READ, null);
                getDepartments({ place_id: row.original?.id });
                getAvailableManagers();
            }
        } else {
            //CREATE
            const res = await createDepartment(parsedData);
            if (res?.data) {
                toast.success(t('Dział został dodany'));
                setState(OPERATIONS.READ, null);
                getDepartments({ place_id: row.original?.id });
                getAvailableManagers();
            }
        }
    };

    const handleSubmitDelete = async () => {
        //DELETE
        const res = await deleteDepartment(activeID);
        if (res?.data) {
            toast.success(t('Dział został usunięty'));
            setState(OPERATIONS.READ, null);
            getDepartments({ place_id: row.original?.id });
            getAvailableManagers();
        }
    };

    if (professionsLoading || departmentsLoading || createDepartmentLoading || updateDepartmentLoading || deleteDepartmentLoading || availableManagersLoading) {
        return (
            <Row center="xs">
                <Col>
                    <LoaderImage />
                </Col>
            </Row>
        );
    }

    return (
        <StyledRowSubComponent>
            <Divider customStyle={styles.topDivider} />
            <Row>
                <Col xs={6}>
                    <Box customStyle={styles.boxLeft}>
                        <Input
                            type="text"
                            label={t("Nazwa obiektu")}
                            value={row.original?.name}
                            readonly
                        />
                        <Map
                            currentLocation={{
                                address: row.original?.address,
                                lat: row.original?.lat,
                                lng: row.original?.lng,
                            }}
                            readonly
                        />
                    </Box>
                </Col>
                <Col xs={6}>
                    <Box customStyle={styles.boxRight}>
                        <ImageUpload
                            label={t("Logo")}
                            imageURL={getImageUrl(row.original?.logo?.url)}
                            readonly
                        />
                        <TextArea
                            label={t("Opis")}
                            value={row.original?.description}
                            fullWidth
                            height={94}
                            readonly
                        />
                        <Input
                            type="text"
                            label={t("Adres www")}
                            value={row.original?.url}
                            readonly
                        />
                        <Input
                            type="text"
                            label={t("Administrator obiektu")}
                            value={`${row.original?.user_supplier?.user?.first_name || ''} ${row.original?.user_supplier?.user?.last_name || ''}`}
                            readonly
                        />
                    </Box>
                </Col>
            </Row>
            {(departments && departments.length > 0) && (
                <TableDepartments
                    departments={departments}
                    handleClickDuplicate={handleClickDuplicate}
                    handleClickEdit={handleClickEdit}
                    handleClickDelete={handleClickDelete}
                />
            )}
            <ButtonGroup customStyle={styles.buttons}>
                <Button
                    startIcon={
                        <Icon
                            nam="add"
                            width={12}
                            height={12}
                        />
                    }
                    onClick={handleClickAdd}
                >
                    {t("Dodaj dział")}
                </Button>
            </ButtonGroup>
            {
                role === ROLES.SUPERADMIN &&
                <>
                    <Divider customStyle={styles.dividerBottom} />
                    <Row end="xs">
                        <Col>
                            <ButtonGroup
                                gutters={10}
                            >
                                <Button
                                    variant="text"
                                    startIcon={
                                        <Icon name="edit" width={12} height={12} />
                                    }
                                    onClick={onClickEdit(row.original?.id)}
                                >
                                    {t("Edytuj obiekt")}
                        </Button>
                                <Button
                                    variant="text"
                                    startIcon={
                                        <Icon
                                            name="close"
                                            width={12}
                                            height={12}
                                        />
                                    }
                                    onClick={onClickRemove(row.original?.id)}
                                >
                                    {t("Usuń obiekt")}
                        </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </>
            }
            <Modal
                open={operation === OPERATIONS.ADD || operation === OPERATIONS.EDIT || operation === OPERATIONS.DUPLICATE}
            >
                <FormDepartment
                    loading={professionsLoading || createDepartmentLoading || updateDepartmentLoading}
                    professionsHints={professions}
                    edit={operation === OPERATIONS.EDIT}
                    place={row.original}
                    availableManagers={availableManagers}
                    initialValues={operation === OPERATIONS.EDIT ? active?.form_values_edit : operation === OPERATIONS.DUPLICATE ? active?.form_values_duplicate : {}}
                    onSubmit={handleSubmit}
                    onCancel={handleClickCancelModal}
                />
            </Modal>
            <Modal
                open={operation === OPERATIONS.DELETE}
            >
                <FormConfirmDelete
                    onSubmit={handleSubmitDelete}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
        </StyledRowSubComponent>
    )
};

export default RowSubComponent;
